import { FC, Fragment, useState } from 'react';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	Alert,
	Button,
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Radio,
	Row,
	Select,
	Table,
	Typography,
} from 'antd';
import {
	DeleteOutlined,
	EditOutlined,
	FileSearchOutlined,
} from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import TextArea from 'antd/es/input/TextArea';
import ProposerModal, { useProposerModal } from './modals/ProposerModal';
import ComitteModal, { useComitteModal } from './modals/ComitteModal';
import { LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getNAPAuthorityMember,
	getNAPWholesaleDetail,
	NAPDetail,
	NAPState,
	NAPSubmitWholesale,
	NAPSubmitWholesaleJF,
	NAPUser,
	NAPVerifyWholesale,
	NAPVerifyWholesaleJF,
	NAPWholesaleJFPayload,
	NAPWholesalePayload,
} from '@/libs/fleet-api/services/nap';
import dayjs, { Dayjs } from 'dayjs';
import { useForm } from '@/libs/antd/form';
import rule from '@/libs/antd/rules';
import { inputCurrency } from '@/libs/antd/input-number';
import { formatCurrency } from '@/libs/antd/formater';
import { useUserData } from '@/libs/fleet-api/hooks';
import EmploymentContractModal, {
	useEmploymentContractModal,
} from './modals/EmploymentContractModal';
import { ProspectEmploymentContract } from '@/libs/fleet-api/services/types';
import { FleetFile } from '@/libs/fleet-api/fetcher';
import { useModals } from '@/components/modals/ModalProvider';

type NAPWholesaleForm = Omit<
	NAPWholesalePayload,
	'additionalProposers' | 'additionalRecommendingCommittees'
> & {
	additionalProposers: NAPUser[];
	additionalRecommendingCommittees: NAPUser[];
};

const NAPWholesale: FC<{
	plafonType: NAPDetail['plafonType'];
	napState?: NAPState;
	prospectId: string;
	customerId: string;
	onSubmitded: () => void;
}> = ({ plafonType, napState, prospectId, customerId, onSubmitded }) => {
	const { openPreviewModal } = useModals();
	const { groupName } = useUserData() ?? {};
	const { form, value, fieldName } = useForm<NAPWholesaleForm>();

	const getWholesaleDetailQuery = useQuery({
		queryKey: ['getNAPWholesaleDetail', prospectId] as const,
		queryFn: async ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			const data = await getNAPWholesaleDetail(id);
			data.data.restruGpStatusOptions =
				data.data.restruGpStatusOptions.reverse();

			data.data.restruGpCovidStatusOptions =
				data.data.restruGpCovidStatusOptions.reverse();

			return data;
		},
	});

	const { data } = getWholesaleDetailQuery.data ?? {};
	const isReadOnly =
		napState?.action === 'VIEW' || napState?.action === 'DISABLED';

	const getNAPAuthorityMemberQuery = useQuery({
		queryKey: ['getNAPAuthorityMember', value?.authorityLevel] as const,
		queryFn: async ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			const res = await getNAPAuthorityMember({
				authorityLevelId: String(id),
				prospectId,
			});

			form.setFieldValue(fieldName('additionalProposers'), [
				...res.data.proposers,
				...(value?.additionalProposers?.filter((data) => data.isAdditional) ??
					[]),
			]);

			form.setFieldValue(fieldName('additionalRecommendingCommittees'), [
				...res.data.committees,
				...(value?.additionalRecommendingCommittees?.filter(
					(data) => data.isAdditional,
				) ?? []),
			]);
			return res;
		},
	});

	const totalBusinessGroup = value?.totalFacilityRequest?.businessGroups.reduce(
		(acc, curr) => {
			return {
				applicationLimit: acc.applicationLimit + (curr.applicationLimit ?? 0),
				existingLimit: acc.existingLimit + (curr.existingLimit ?? 0),
				totalLimit: acc.totalLimit + (curr.totalLimit ?? 0),
			};
		},
		{ applicationLimit: 0, existingLimit: 0, totalLimit: 0 } as Pick<
			NAPWholesalePayload['totalFacilityRequest']['businessGroups'][number],
			'applicationLimit' | 'existingLimit' | 'totalLimit'
		>,
	);

	const proposerModal = useProposerModal((type, payload) => {
		const existings = value?.additionalProposers ?? [];
		const isExist = !!existings.find(({ id }) => id === payload.user.id);
		if (isExist) return;

		if (type === 'edit') {
			form.setFieldValue(
				fieldName('additionalProposers'),
				existings.map((data, index) => {
					if (index === payload.index) {
						return { ...payload.user, isAdditional: true };
					}

					return data;
				}),
			);
		} else {
			form.setFieldValue(fieldName('additionalProposers'), [
				...existings,
				{ ...payload.user, isAdditional: true } as NAPUser,
			]);
		}
	});

	const comitteModal = useComitteModal((type, payload) => {
		const existings = value?.additionalRecommendingCommittees ?? [];
		const isExist = !!existings.find(({ id }) => id === payload.user.id);
		if (isExist) return;

		if (type === 'edit') {
			form.setFieldValue(
				fieldName('additionalRecommendingCommittees'),
				existings.map((data, index) => {
					if (index === payload.index) {
						return { ...payload.user, isAdditional: true };
					}

					return data;
				}),
			);
		} else {
			form.setFieldValue(fieldName('additionalRecommendingCommittees'), [
				...existings,
				{ ...payload.user, isAdditional: true } as NAPUser,
			]);
		}
	});

	const employmentContractModal = useEmploymentContractModal();

	const submitWholesaleMutation = useMutation({
		mutationFn: NAPSubmitWholesale,
	});

	const verifyWholesaleMutation = useMutation({
		mutationFn: NAPVerifyWholesale,
	});

	const submitWholesaleJFMutation = useMutation({
		mutationFn: NAPSubmitWholesaleJF,
	});

	const verifyWholesaleJFMutation = useMutation({
		mutationFn: NAPVerifyWholesaleJF,
	});

	const [dateKey, setDateKey] = useState(Date.now());

	if (getWholesaleDetailQuery.isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const formFragment = () => {
		const formFragmentNJF = (
			<Fragment>
				<Form.Item label="Risalah Pipeline">
					<DatePicker
						style={{ width: 300 }}
						value={data?.pipelineSummaryDate && dayjs(data.pipelineSummaryDate)}
						disabled
					/>
				</Form.Item>

				<Divider />

				<Form.Item label="Tipe Debitur">
					<Checkbox.Group
						style={{ columnGap: 50, rowGap: 10 }}
						value={data?.debtorType ? [data.debtorType.id] : []}
					>
						{data?.debtorTypeOptions.map((data) => (
							<Checkbox
								key={data.id}
								value={data.id}
								disabled
							>
								{data.value}
							</Checkbox>
						))}
					</Checkbox.Group>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Dasar Usulan"
					labelCol={{ style: { alignSelf: 'flex-start', width: 200 } }}
					required
				>
					<Form.Item
						name={fieldName('debtorFappDate')}
						initialValue={data?.debtorFappDate}
						label="FAPP Calon Debitur/Debitur"
						labelCol={{ span: 12 }}
						style={{ marginBottom: 20 }}
						getValueProps={(value) => ({ value: value && dayjs(value) })}
						normalize={(value) => value && dayjs(value).utc(true).toISOString()}
						rules={[rule.required()]}
					>
						<DatePicker disabled={isReadOnly} />
					</Form.Item>
					<Form.Item
						name={fieldName('referralFormDate')}
						initialValue={data?.referralFormDate}
						label="Form Referral Calon Debitur/Debitur"
						labelCol={{ span: 12 }}
						getValueProps={(value) => ({ value: value && dayjs(value) })}
						normalize={(value) => value && dayjs(value).utc(true).toISOString()}
						rules={
							data?.debtorType.id === 1 || data?.debtorType.id === 5
								? []
								: [rule.required()]
						}
					>
						<DatePicker disabled={isReadOnly} />
					</Form.Item>
				</Form.Item>

				<Divider />

				<Form.Item
					name={fieldName('bmriReferralInformation')}
					initialValue={data?.bmriReferralInformation}
					label="Informasi Referral dari BMRI"
				>
					<Input disabled={isReadOnly} />
				</Form.Item>

				<Form.Item label={' '}>
					<Alert
						message="Jika debitur adalah Captive BMRI (Jika Ada)"
						type="info"
						showIcon
						style={{ marginTop: 10 }}
					/>
				</Form.Item>

				<Divider />
			</Fragment>
		);

		const formFragmentJF = (
			<Fragment>
				<Typography.Title level={3}>Informasi Umum</Typography.Title>

				<Row gutter={50}>
					<Col span={12}>
						<Form.Item
							label="Nama Customer"
							rules={[rule.required()]}
						>
							<Input
								value={data?.applicantName}
								disabled
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name="establishmentYear"
							label="Tahun Berdiri"
							getValueProps={(value) => ({ value: value && dayjs(value) })}
							normalize={(value) => value && dayjs(value).year().toString()}
							rules={[rule.required()]}
							initialValue={data?.establishmentYear}
						>
							<DatePicker.YearPicker disabled={isReadOnly} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={50}>
					<Col span={12}>
						<Form.Item
							label="Jenis Industri"
							rules={[rule.required()]}
						>
							<Input
								value={data?.industrySector}
								disabled
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name="operationalYear"
							label="Tahun Beroperasi"
							getValueProps={(value) => ({ value: value && dayjs(value) })}
							normalize={(value) => value && dayjs(value).year().toString()}
							rules={[rule.required()]}
							initialValue={data?.operationalYear}
						>
							<DatePicker.YearPicker disabled={isReadOnly} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={50}>
					<Col span={12}>
						<Form.Item
							label="Nama Group Usaha"
							rules={[rule.required()]}
						>
							<Input
								value={data?.businessGroupName}
								disabled
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Pemberi Referral BMRI"
							rules={[rule.required()]}
						>
							<Input
								value={data?.bmriReferralProviderName}
								disabled
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={50}>
					<Col span={12}>
						<Form.Item
							label="No. Consent Approval"
							name="consentApprovalNumber"
							initialValue={data?.consentApprovalNumber}
							rules={[rule.required()]}
						>
							<Input disabled={isReadOnly} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Tanggal Consent Approval"
							name="consentApprovalDate"
							initialValue={data?.consentApprovalDate}
							getValueProps={(value) => ({ value: value && dayjs(value) })}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
							rules={[rule.required()]}
						>
							<DatePicker disabled={isReadOnly} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={50}>
					<Col span={12}>
						<Form.Item
							label="Status Restru/GP di MTF"
							required
							labelCol={{ style: { width: 200, alignSelf: 'flex-start' } }}
						>
							<div style={{ marginBlock: 10 }}>
								<Form.Item
									name="restruGpStatus"
									rules={[rule.required()]}
									initialValue={data?.restruGpStatus?.id}
									noStyle
								>
									<Radio.Group style={{ columnGap: 50, rowGap: 10 }}>
										{data?.restruGpStatusOptions.map((data) => (
											<Radio
												key={data.id}
												value={data.id}
												disabled={isReadOnly}
												onClick={() => {
													if (data.id === 0) {
														form.setFieldValue('restruGpCovidStatus', null);
														form.setFieldValue('restruGpDueDate', null);
													}
												}}
											>
												{data.value}
											</Radio>
										))}
									</Radio.Group>
								</Form.Item>
							</div>
							<div>
								<Form.Item
									name="restruGpCovidStatus"
									rules={
										'restruGpStatus' in value! &&
										(value.restruGpStatus as number) === 1
											? [rule.required()]
											: []
									}
									initialValue={data?.restruGpCovidStatus?.id}
									noStyle
								>
									<Radio.Group
										style={{
											display: 'flex',
											rowGap: 10,
											flexDirection: 'column',
										}}
									>
										{data?.restruGpCovidStatusOptions.map((data) => (
											<Radio
												key={data.id}
												value={data.id}
												disabled={
													isReadOnly ||
													('restruGpStatus' in value! &&
														(value.restruGpStatus as number) === 0)
												}
											>
												{data.value}
											</Radio>
										))}
									</Radio.Group>
								</Form.Item>
							</div>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Jatuh Tempo Restru/GP"
							name="restruGpDueDate"
							initialValue={data?.restruGpDueDate}
							getValueProps={(value) => ({ value: value && dayjs(value) })}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
							rules={
								'restruGpStatus' in value! && value.restruGpStatus !== 1
									? []
									: [rule.required()]
							}
						>
							<DatePicker
								disabled={
									isReadOnly ||
									('restruGpStatus' in value! && value.restruGpStatus !== 1)
								}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
			</Fragment>
		);

		return (
			<Fragment>
				<Form.Item
					name={fieldName('napId')}
					initialValue={data?.napId}
					noStyle
				/>

				<Form.Item
					name={fieldName('wholesaleId')}
					initialValue={data?.wholesaleId}
					noStyle
				/>

				<Form.Item
					name={fieldName('prospectId')}
					initialValue={prospectId}
					noStyle
				/>

				<Form.Item label="Perihal">
					<Checkbox.Group
						style={{ columnGap: 50, rowGap: 10 }}
						value={data?.subject?.id ? [data.subject.id] : []}
					>
						{data?.subjectOptions?.map((data) => (
							<Checkbox
								key={data.id}
								value={data.id}
								disabled
							>
								{data.value}
							</Checkbox>
						))}
					</Checkbox.Group>
				</Form.Item>

				<Divider />

				{plafonType === 'NON_JOIN_FINANCING' ? formFragmentNJF : formFragmentJF}

				<Row gutter={50}>
					<Col span={12}>
						<Form.Item
							name={fieldName('authorityLevel')}
							initialValue={data?.authorityLevel?.id}
							label="Kewenangan Memutus"
							rules={[rule.required()]}
						>
							<Select
								disabled={isReadOnly}
								options={
									data?.authorityLevelOptions?.map?.((data) => {
										return { label: data.value, value: data.id };
									}) ?? []
								}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('regulationReference')}
							initialValue={data?.regulationReference}
							label="Berdasarkan dengan nota/ketentuan"
							rules={[rule.required()]}
						>
							<Select
								disabled={isReadOnly}
								options={
									data
										? data.regulationReferenceOptions.map((item) => {
												return { value: item.value };
											})
										: []
								}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />
			</Fragment>
		);
	};

	const proposerFragment = (
		<Fragment>
			<Row style={{ marginBottom: 20 }}>
				<Col flex="auto">
					<strong>Pengusul</strong>
				</Col>
				<Col flex="none">
					<Button
						style={{
							borderRadius: 50,
							fontSize: 12,
							height: 24,
							fontWeight: 700,
							...(napState?.action !== 'VIEW' && {
								borderColor: '#1557BC',
								color: '#1557BC',
							}),
						}}
						icon={'+'}
						onClick={() => proposerModal.open({ type: 'new' })}
						disabled={isReadOnly}
					>
						Add Another
					</Button>
				</Col>
			</Row>

			<Form.Item
				name={fieldName('additionalProposers')}
				initialValue={data?.proposers ?? []}
				noStyle
			>
				<Table
					bordered
					pagination={false}
					dataSource={value?.additionalProposers?.map((data, key) => {
						return { ...data, key };
					})}
					loading={getNAPAuthorityMemberQuery.isFetching}
					columns={[
						{
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={
										!record.isAdditional || isReadOnly
											? { color: '#bababa' }
											: {}
									}
									onClick={(e) => {
										e.preventDefault();
										if (record.isAdditional && !isReadOnly) {
											proposerModal.open({
												type: 'edit',
												data: { user: record, index },
											});
										}
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={
										!record.isAdditional || isReadOnly
											? { color: '#bababa' }
											: {}
									}
									onClick={(e) => {
										e.preventDefault();
										if (record.isAdditional && !isReadOnly) {
											form.setFieldValue(
												fieldName('additionalProposers'),
												value?.additionalProposers?.filter(
													(_, i) => i !== index,
												),
											);
										}
									}}
								>
									<DeleteOutlined />
								</a>
							),
						},
						{ title: 'Nama', render: (_, record) => record.name },
						{ title: 'Jabatan', render: (_, record) => record.role },
					]}
				/>
			</Form.Item>

			<Divider />
		</Fragment>
	);

	const committeFragment = (
		<Fragment>
			<Row style={{ marginBottom: 20 }}>
				<Col flex="auto">
					<strong>Recommending Committe</strong>
				</Col>
				<Col flex="none">
					<Button
						style={{
							borderRadius: 50,
							fontSize: 12,
							height: 24,
							fontWeight: 700,
							...(!isReadOnly && { borderColor: '#1557BC', color: '#1557BC' }),
						}}
						disabled={isReadOnly}
						icon={'+'}
						onClick={() => comitteModal.open({ type: 'new' })}
					>
						Add Another
					</Button>
				</Col>
			</Row>

			<Form.Item
				name={fieldName('additionalRecommendingCommittees')}
				initialValue={data?.recommendingCommittees ?? []}
				noStyle
			>
				<Table
					bordered
					pagination={false}
					dataSource={value?.additionalRecommendingCommittees?.map(
						(data, key) => {
							return { ...data, key };
						},
					)}
					loading={getNAPAuthorityMemberQuery.isFetching}
					columns={[
						{
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={
										!record.isAdditional || isReadOnly
											? { color: '#bababa' }
											: {}
									}
									onClick={(e) => {
										e.preventDefault();
										if (record.isAdditional && !isReadOnly) {
											comitteModal.open({
												type: 'edit',
												data: { user: record, index },
											});
										}
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={
										!record.isAdditional || isReadOnly
											? { color: '#bababa' }
											: {}
									}
									onClick={(e) => {
										e.preventDefault();
										if (record.isAdditional && !isReadOnly) {
											form.setFieldValue(
												fieldName('additionalRecommendingCommittees'),
												value?.additionalRecommendingCommittees?.filter(
													(_, i) => index !== i,
												),
											);
										}
									}}
								>
									<DeleteOutlined />
								</a>
							),
						},
						{ title: 'Nama', render: (_, record) => record.name },
						{ title: 'Jabatan', render: (_, record) => record.role },
					]}
				/>
			</Form.Item>

			<Divider />
		</Fragment>
	);

	const debtorFragment = (
		<Fragment>
			<FleetTitle>
				Total Permohonan Fasilitas a.n Calon Debitur dan Group Usaha (Jika Ada)
			</FleetTitle>

			<Alert
				message={
					<ul>
						<li>Jika plafon aktif diinput nominal plafon</li>
						<li>Jika plafon expired diinput Outstanding Principal</li>
					</ul>
				}
				type="info"
				showIcon
				style={{ marginBlock: 20 }}
			/>

			<Table
				bordered
				pagination={false}
				rowHoverable={false}
				dataSource={[
					...(data?.totalFacilityRequest?.debtorPlafons ?? []).map(
						(data, index) => {
							return {
								...data,
								key: index + 1,
								name: !index ? 'Fasilitas a.n calon debitur' : '',
							};
						},
					),
					{
						key: 'total',
						name: 'Total Debitur',
						expiredDate: '',
						plafonType: null,
						applicationLimit: 0,
						existingLimit: 0,
						totalLimit: 0,
					},
				]}
				onRow={(data) => {
					if (data.key === 'total') {
						return { style: { background: '#E8F1FF' } };
					}

					return {};
				}}
				columns={[
					{
						title: 'Uraian',
						render: (_, record) => {
							if (record.key === 'total') return <strong>{record.name}</strong>;
							return record.name;
						},
					},
					{
						title: 'Jenis Plafon',
						render: (_, record, index) => {
							return (
								<Form.Item
									name={fieldName([
										'totalFacilityRequest',
										'debtorPlafons',
										index,
										'plafonType',
									])}
									initialValue={record.plafonType?.id}
								>
									{record.plafonType?.value}
								</Form.Item>
							);
						},
					},
					{
						title: 'Exp Date',
						render: (_, record, index) => {
							if (record.key !== 'total') {
								return (
									<Form.Item
										name={fieldName([
											'totalFacilityRequest',
											'debtorPlafons',
											index,
											'expiredDate',
										])}
										initialValue={record.expiredDate}
										getValueProps={(value) => ({
											value: value && dayjs(value),
										})}
										normalize={(value) =>
											value && dayjs(value).utc(true).toISOString()
										}
										noStyle
									>
										<DatePicker disabled={isReadOnly} />
									</Form.Item>
								);
							}
						},
					},
					{
						title: 'Limit Existing',
						width: 200,
						render: (_, record, index) => {
							if (record.key === 'total') {
								const debtorPlafons =
									value?.totalFacilityRequest?.debtorPlafons?.map(
										(d) => d.existingLimit,
									) ?? [];

								const total = debtorPlafons
									?.filter((d) => d)
									.reduce((acc, curr) => acc + curr, 0);

								return (
									<InputNumber
										{...inputCurrency}
										value={total}
										disabled
									/>
								);
							}

							return (
								<Form.Item
									name={fieldName([
										'totalFacilityRequest',
										'debtorPlafons',
										index,
										'existingLimit',
									])}
									initialValue={record.existingLimit}
									noStyle
								>
									<InputNumber
										{...inputCurrency}
										onChange={() => {
											const { totalFacilityRequest } = form.getFieldsValue();
											const existingLimit =
												totalFacilityRequest?.debtorPlafons?.[index]
													.existingLimit ?? 0;

											const applicationLimit =
												totalFacilityRequest?.debtorPlafons?.[index]
													.applicationLimit ?? 0;

											form.setFieldValue(
												fieldName([
													'totalFacilityRequest',
													'debtorPlafons',
													index,
													'totalLimit',
												]),
												existingLimit + applicationLimit,
											);
										}}
										disabled={isReadOnly}
									/>
								</Form.Item>
							);
						},
					},
					{
						title: 'Limit Pengajuan',
						width: 200,
						render: (_, record, index) => {
							if (record.key === 'total') {
								const debtorPlafons =
									value?.totalFacilityRequest?.debtorPlafons?.map(
										(d) => d.applicationLimit,
									) ?? [];

								const total = debtorPlafons
									?.filter((d) => d)
									.reduce((acc, curr) => acc + curr, 0);

								return (
									<InputNumber
										{...inputCurrency}
										value={total}
										disabled
									/>
								);
							}

							return (
								<Form.Item
									name={fieldName([
										'totalFacilityRequest',
										'debtorPlafons',
										index,
										'applicationLimit',
									])}
									initialValue={record.applicationLimit}
									noStyle
								>
									<InputNumber
										{...inputCurrency}
										disabled={isReadOnly}
										onChange={() => {
											const { totalFacilityRequest } = form.getFieldsValue();
											const existingLimit =
												totalFacilityRequest?.debtorPlafons?.[index]
													.existingLimit ?? 0;

											const applicationLimit =
												totalFacilityRequest?.debtorPlafons?.[index]
													.applicationLimit ?? 0;

											form.setFieldValue(
												fieldName([
													'totalFacilityRequest',
													'debtorPlafons',
													index,
													'totalLimit',
												]),
												existingLimit + applicationLimit,
											);
										}}
									/>
								</Form.Item>
							);
						},
					},
					{
						title: 'Total Limit',
						width: 200,
						render: (_, record, index) => {
							if (record.key === 'total') {
								const debtorPlafons =
									value?.totalFacilityRequest?.debtorPlafons?.map(
										(d) => d.totalLimit,
									) ?? [];

								const total = debtorPlafons
									?.filter((d) => d)
									.reduce((acc, curr) => acc + curr, 0);

								return (
									<InputNumber
										{...inputCurrency}
										value={total}
										disabled
									/>
								);
							}

							return (
								<Form.Item
									name={fieldName([
										'totalFacilityRequest',
										'debtorPlafons',
										index,
										'totalLimit',
									])}
									initialValue={record.totalLimit}
									noStyle
								>
									<InputNumber
										{...inputCurrency}
										disabled
									/>
								</Form.Item>
							);
						},
					},
				]}
			/>

			<Divider />
		</Fragment>
	);

	// TOTAL NJF
	const totalExistingLimitBusinessGroupNJF =
		value?.totalFacilityRequest?.businessGroups
			.filter((d) => d.plafonType === 1)
			.reduce((total, curr) => {
				return total + curr.existingLimit;
			}, 0) ?? 0;

	const totalExistingLimitDebtorNJF =
		value?.totalFacilityRequest?.debtorPlafons?.find(
			(data) => data.plafonType === 1,
		)?.existingLimit ?? 0;

	const totalExistingLimitNJF =
		totalExistingLimitBusinessGroupNJF + totalExistingLimitDebtorNJF;

	const totalApplicationLimitBusinessGroupNJF =
		value?.totalFacilityRequest?.businessGroups
			.filter((d) => d.plafonType === 1)
			.reduce((total, curr) => {
				return total + curr.applicationLimit;
			}, 0) ?? 0;

	const totalApplicationLimitDebtorNJF =
		value?.totalFacilityRequest?.debtorPlafons?.find(
			(data) => data.plafonType === 1,
		)?.applicationLimit ?? 0;

	const totalApplicationLimitNJF =
		totalApplicationLimitBusinessGroupNJF + totalApplicationLimitDebtorNJF;

	// TOTAL JF
	const totalExistingLimitBusinessGroupJF =
		value?.totalFacilityRequest?.businessGroups
			.filter((d) => d.plafonType === 2)
			.reduce((total, curr) => {
				return total + curr.existingLimit;
			}, 0) ?? 0;

	const totalExistingLimitDebtorJF =
		value?.totalFacilityRequest?.debtorPlafons?.find(
			(data) => data.plafonType === 2,
		)?.existingLimit ?? 0;

	const totalExistingLimitJF =
		totalExistingLimitBusinessGroupJF + totalExistingLimitDebtorJF;

	const totalApplicationLimitBusinessGroupJF =
		value?.totalFacilityRequest?.businessGroups
			.filter((d) => d.plafonType === 2)
			.reduce((total, curr) => {
				return total + curr.applicationLimit;
			}, 0) ?? 0;

	const totalApplicationLimitDebtorJF =
		value?.totalFacilityRequest?.debtorPlafons?.find(
			(data) => data.plafonType === 2,
		)?.applicationLimit ?? 0;

	const totalApplicationLimitJF =
		totalApplicationLimitBusinessGroupJF + totalApplicationLimitDebtorJF;

	const businessGroupFragment = (
		<Fragment>
			<Row style={{ marginBottom: 20 }}>
				<Col flex="auto">
					<strong>Group Usaha</strong>
				</Col>
				<Col flex="none">
					<Button
						style={{
							borderRadius: 50,
							fontSize: 12,
							height: 24,
							fontWeight: 700,
							...(!isReadOnly && { borderColor: '#1557BC', color: '#1557BC' }),
						}}
						icon={'+'}
						disabled={isReadOnly}
						onClick={() => {
							form.setFieldValue(
								fieldName(['totalFacilityRequest', 'businessGroups']),
								[
									...(value?.totalFacilityRequest?.businessGroups ?? []),
									{
										key: value!.totalFacilityRequest!.businessGroups.length + 1,
										name: '',
										existingLimit: 0,
										applicationLimit: 0,
										totalLimit: 0,
										expiredDate: undefined,
										plafonType: undefined,
										customerId: undefined,
										isDefault: false,
									},
								],
							);
							setDateKey(Date.now());
						}}
					>
						Add Another
					</Button>
				</Col>
			</Row>

			<Form.Item
				name={fieldName(['totalFacilityRequest', 'businessGroups'])}
				initialValue={
					data?.totalFacilityRequest?.businessGroups?.map((data) => {
						return {
							name: data.name,
							applicationLimit: data.applicationLimit,
							existingLimit: data.existingLimit,
							expiredDate: data.expiredDate,
							plafonType: data.plafonType.id,
							totalLimit: data.totalLimit,
							customerId: data.customerId,
							isDefault: data.isDefault ?? false,
						} as NAPWholesalePayload['totalFacilityRequest']['businessGroups'][number];
					}) ?? []
				}
				noStyle
			/>

			<Table
				bordered
				pagination={false}
				rowHoverable={false}
				dataSource={[
					...(value?.totalFacilityRequest?.businessGroups?.map?.(
						(data, index) => {
							return {
								key: dateKey + index,
								...data,
								isDefault: data.isDefault ?? false,
							};
						},
					) ?? []),
					{ key: 'total-group', name: 'Total Group Usaha' },
					{ key: 'empty-row', name: '' },
					{ key: 'total-njf', name: 'Total Exposure NJF' },
					{ key: 'total-jf', name: 'Total Exposure JF' },
					{ key: 'grand-total', name: 'GRAND TOTAL' },
				]}
				onRow={(data) => {
					if (data.key === 'empty-row')
						return { style: { background: '#FAFAFA' } };

					if (data.key === 'total-group' || data.key === 'grand-total') {
						return { style: { background: '#E8F1FF' } };
					}

					if (data.key === 'total-jf' || data.key === 'total-njf') {
						return { style: { background: '#FFF8DD' } };
					}

					return {};
				}}
				columns={[
					{
						title: 'Delete',
						width: 100,
						align: 'center',
						render: (_, record, index) => {
							if (typeof record.key !== 'number') {
								if (record.key === 'empty-row')
									return { props: { colSpan: 7 } };
								return {
									children: <strong>{record.name}</strong>,
									props: { style: { textAlign: 'left' }, colSpan: 4 },
								};
							}

							if (
								isReadOnly ||
								(value?.totalFacilityRequest?.businessGroups[index].isDefault ??
									false)
							)
								return <DeleteOutlined style={{ color: '#bababa' }} />;

							return (
								<a
									onClick={(e) => {
										e.preventDefault();
										form.setFieldValue(
											fieldName(['totalFacilityRequest', 'businessGroups']),
											value?.totalFacilityRequest?.businessGroups?.filter(
												(_, i) => i !== index,
											),
										);
										setDateKey(Date.now());
									}}
								>
									<DeleteOutlined />
								</a>
							);
						},
					},
					{
						title: 'Nama Debitur',
						render: (_, record, index) => {
							if (typeof record.key === 'string') {
								return {
									children: <strong>{record.name}</strong>,
									props: { colSpan: 0 },
								};
							}

							return (
								<Input
									placeholder="Nama Debitur..."
									disabled={
										isReadOnly ||
										(value?.totalFacilityRequest?.businessGroups[index]
											.isDefault ??
											false)
									}
									value={form.getFieldValue(
										fieldName([
											'totalFacilityRequest',
											'businessGroups',
											index,
											'name',
										]),
									)}
									onChange={(e) => {
										form.setFieldValue(
											fieldName([
												'totalFacilityRequest',
												'businessGroups',
												index,
												'name',
											]),
											e.target.value,
										);
									}}
								/>
							);
						},
					},
					{
						title: 'Jenis Plafon',
						render: (_, record, index) => {
							if (typeof record.key === 'string')
								return { props: { colSpan: 0 } };

							if (typeof record.key === 'number' && 'plafonType' in record) {
								return (
									<Select
										style={{ width: 160 }}
										value={record.plafonType}
										options={
											data?.plafonTypeOptions.map((data) => {
												return { label: data.value, value: data.id };
											}) ?? []
										}
										disabled={
											isReadOnly ||
											(value?.totalFacilityRequest?.businessGroups[index]
												.isDefault ??
												false)
										}
										onSelect={(plafonType) => {
											form.setFieldValue(
												fieldName([
													'totalFacilityRequest',
													'businessGroups',
													index,
													'plafonType',
												]),
												plafonType,
											);
										}}
									/>
								);
							}
						},
					},
					{
						title: 'Exp Date',
						render: (_, record, index) => {
							if (typeof record.key === 'string')
								return { props: { colSpan: 0 } };

							if (typeof record.key === 'number' && 'expiredDate' in record) {
								return (
									<DatePicker
										value={record.expiredDate && dayjs(record.expiredDate)}
										style={{ width: 130 }}
										disabled={isReadOnly}
										onChange={(date) => {
											form.setFieldValue(
												fieldName([
													'totalFacilityRequest',
													'businessGroups',
													index,
													'expiredDate',
												]),
												(date as Dayjs).utc(true).toISOString(),
											);
										}}
									/>
								);
							}
						},
					},
					{
						title: 'Limit Existing',
						width: 160,
						render: (_, record, index) => {
							if (record.key === 'empty-row') return { props: { colSpan: 0 } };

							const totalExistingLimit = totalBusinessGroup?.existingLimit ?? 0;

							if (record.key === 'total-group') {
								return (
									<Input
										value={formatCurrency(totalExistingLimit)}
										disabled
									/>
								);
							}

							if (record.key === 'total-njf') {
								return (
									<Input
										value={formatCurrency(totalExistingLimitNJF)}
										disabled
									/>
								);
							}

							if (record.key === 'total-jf') {
								return (
									<Input
										value={formatCurrency(totalExistingLimitJF)}
										disabled
									/>
								);
							}

							if (record.key === 'grand-total') {
								return (
									<Input
										value={formatCurrency(
											totalExistingLimitNJF + totalExistingLimitJF,
										)}
										disabled
									/>
								);
							}

							return (
								<InputNumber
									{...inputCurrency}
									disabled={isReadOnly}
									value={form.getFieldValue(
										fieldName([
											'totalFacilityRequest',
											'businessGroups',
											index,
											'existingLimit',
										]),
									)}
									onChange={(existingLimit) => {
										form.setFieldValue(
											fieldName([
												'totalFacilityRequest',
												'businessGroups',
												index,
												'existingLimit',
											]),
											existingLimit,
										);

										const { totalFacilityRequest } = form.getFieldsValue();
										const applicationLimit =
											totalFacilityRequest.businessGroups[index]
												.applicationLimit ?? 0;

										form.setFieldValue(
											fieldName([
												'totalFacilityRequest',
												'businessGroups',
												index,
												'totalLimit',
											]),
											Number(existingLimit) + applicationLimit,
										);
									}}
								/>
							);
						},
					},
					{
						title: 'Limit Pengajuan',
						width: 160,
						render: (_, record, index) => {
							if (record.key === 'empty-row') return { props: { colSpan: 0 } };

							const totalApplicationLimit =
								totalBusinessGroup?.applicationLimit ?? 0;

							if (record.key === 'total-group') {
								return (
									<Input
										value={formatCurrency(totalApplicationLimit)}
										disabled
									/>
								);
							}

							if (record.key === 'total-njf') {
								return (
									<Input
										value={formatCurrency(totalApplicationLimitNJF)}
										disabled
									/>
								);
							}

							if (record.key === 'total-jf') {
								return (
									<Input
										value={formatCurrency(totalApplicationLimitJF)}
										disabled
									/>
								);
							}

							if (record.key === 'grand-total') {
								return (
									<Input
										value={formatCurrency(
											totalApplicationLimitNJF + totalApplicationLimitJF,
										)}
										disabled
									/>
								);
							}

							return (
								<InputNumber
									{...inputCurrency}
									disabled={isReadOnly}
									value={form.getFieldValue(
										fieldName([
											'totalFacilityRequest',
											'businessGroups',
											index,
											'applicationLimit',
										]),
									)}
									onChange={(applicationLimit) => {
										form.setFieldValue(
											fieldName([
												'totalFacilityRequest',
												'businessGroups',
												index,
												'applicationLimit',
											]),
											applicationLimit,
										);

										const { totalFacilityRequest } = form.getFieldsValue();
										const existingLimit =
											totalFacilityRequest.businessGroups[index]
												.existingLimit ?? 0;

										form.setFieldValue(
											fieldName([
												'totalFacilityRequest',
												'businessGroups',
												index,
												'totalLimit',
											]),
											Number(applicationLimit) + existingLimit,
										);
									}}
								/>
							);
						},
					},
					{
						title: 'Total Limit',
						width: 160,
						render: (_, record, index) => {
							if (record.key === 'empty-row') return { props: { colSpan: 0 } };

							if (record.key === 'total-group') {
								return (
									<Input
										value={formatCurrency(totalBusinessGroup?.totalLimit ?? 0)}
										disabled
									/>
								);
							}

							if (record.key === 'total-njf') {
								return (
									<Input
										value={formatCurrency(
											totalExistingLimitNJF + totalApplicationLimitNJF,
										)}
										disabled
									/>
								);
							}

							if (record.key === 'total-jf') {
								return (
									<Input
										value={formatCurrency(
											totalExistingLimitJF + totalApplicationLimitJF,
										)}
										disabled
									/>
								);
							}

							if (record.key === 'grand-total') {
								return (
									<Input
										value={formatCurrency(
											totalExistingLimitNJF +
												totalExistingLimitJF +
												totalApplicationLimitNJF +
												totalApplicationLimitJF,
										)}
										disabled
									/>
								);
							}

							return (
								<InputNumber
									{...inputCurrency}
									value={form.getFieldValue(
										fieldName([
											'totalFacilityRequest',
											'businessGroups',
											index,
											'totalLimit',
										]),
									)}
									disabled
								/>
							);
						},
					},
				]}
			/>
		</Fragment>
	);

	const contractFragment = (
		<Fragment>
			<Row style={{ marginTop: 20, marginBottom: 20 }}>
				<Col flex="auto">
					<Typography.Title level={3}>Data Bouwheer</Typography.Title>
				</Col>
				<Col flex="none">
					<Button
						style={{
							borderRadius: 50,
							fontSize: 12,
							height: 24,
							fontWeight: 700,
							borderColor: '#1557BC',
							color: '#1557BC',
						}}
						icon={'+'}
						disabled={isReadOnly}
						onClick={async () => {
							const newData = await employmentContractModal.open();
							if (!newData) return;
							form.setFieldValue('contracts', [
								...('contracts' in value!
									? (value?.contracts as ProspectEmploymentContract[])
									: []),
								newData,
							]);
						}}
					>
						Add Another
					</Button>
				</Col>
			</Row>

			<Form.Item
				name="contracts"
				initialValue={data?.contracts}
				noStyle
			/>

			<Table
				style={{ marginTop: 20, marginBottom: 20 }}
				scroll={{ x: 'max-content' }}
				bordered
				pagination={false}
				dataSource={
					(value && 'contracts' in value
						? (value.contracts as ProspectEmploymentContract[])
						: []
					).map((data, i) => {
						return { ...data, key: i + 1 };
					}) ?? []
				}
				columns={[
					{
						title: 'Action',
						colSpan: 3,
						width: 100,
						align: 'center',
						render: (_, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									if (!record.contractDocument) return;
									openPreviewModal(record.contractDocument as FleetFile);
								}}
							>
								<FileSearchOutlined />
							</a>
						),
					},
					{
						colSpan: 0,
						width: 100,
						align: 'center',
						render: (_, record, index) => (
							<a
								style={isReadOnly ? { color: '#bababa' } : {}}
								onClick={async (e) => {
									e.preventDefault();
									if (isReadOnly) return;

									const editData = await employmentContractModal.open(record);
									if (!editData) return;

									const contract =
										value && 'contracts' in value
											? (value.contracts as ProspectEmploymentContract[])
											: [];

									form.setFieldValue(
										'contracts',
										contract.map((data, dataIndex) => {
											return index === dataIndex ? editData : data;
										}),
									);
								}}
							>
								<EditOutlined />
							</a>
						),
					},
					{
						colSpan: 0,
						width: 100,
						align: 'center',
						render: (_, __, index) => (
							<a
								style={isReadOnly ? { color: '#bababa' } : {}}
								onClick={(e) => {
									e.preventDefault();
									if (isReadOnly) return;

									const contract =
										value && 'contracts' in value
											? (value.contracts as ProspectEmploymentContract[])
											: [];

									form.setFieldValue(
										'contracts',
										contract.filter((_, i) => index !== i),
									);
								}}
							>
								<DeleteOutlined />
							</a>
						),
					},
					{ title: 'Bouwheer', render: (_, record) => record.bouwheer?.name },
					{ title: 'Bouwheer ID', render: (_, record) => record.bouwheer?.id },
					{
						title: 'Kelas Industri',
						render: (_, record) =>
							record.bouwheer?.industrySector.industryClass,
					},
					{ title: 'Nama Pekerjaan', render: (_, record) => record.jobTitle },
					{ title: 'No. SPK', render: (_, record) => record.spkNumber },
					{
						title: 'Lokasi Pekerjaan',
						render: (_, record) => record.jobLocation,
					},
					{
						title: 'Detail Kontrak',
						width: 300,
						render: (_, record) => record.contractDetail,
					},
					{
						title: 'Nilai Kontrak',
						render: (_, record) => formatCurrency(record.contractValue) ?? '-',
					},
					{
						title: 'Tanggal Mulai',
						render: (_, record) =>
							record.startDate && dayjs(record.startDate).format('DD/MM/YYYY'),
					},
					{
						title: 'Tanggal Berakhir',
						render: (_, record) =>
							record.endDate && dayjs(record.endDate).format('DD/MM/YYYY'),
					},
					{ title: 'Tenor', render: (_, record) => record.tenor },
					{
						title: 'Sistem Pembayaran',
						render: (_, record) => record.paymentMethod?.value,
					},
					{
						title: 'TOP Pembayaran',
						render: (_, record) => record.termOfPayment,
					},
				]}
			/>
			<Divider />
		</Fragment>
	);

	return (
		<Fragment>
			<ProposerModal {...proposerModal} />
			<ComitteModal {...comitteModal} />
			<EmploymentContractModal
				customerId={customerId}
				prospectId={prospectId}
				{...employmentContractModal}
			/>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{ style: { width: 200 } }}
				onFinish={async (values) => {
					if (plafonType === 'JOIN_FINANCING') {
						const payload: NAPWholesaleJFPayload = {
							prospectId,
							wholesaleId: values.wholesaleId,
							napId: values.napId,
							authorityLevel: values.authorityLevel,
							regulationReference: values.regulationReference,
							agingDate: values.agingDate,
							notes: values.notes,
							additionalProposers: values.additionalProposers
								.filter((data) => data.isAdditional)
								.map((data) => {
									return { id: data.id };
								}),
							additionalRecommendingCommittees:
								values.additionalRecommendingCommittees
									.filter((data) => data.isAdditional)
									.map((data) => {
										return { id: data.id };
									}),
							totalFacilityRequest: {
								businessGroups: values.totalFacilityRequest.businessGroups
									.filter((data) => data.name)
									.map((data) => {
										return {
											name: data.name,
											applicationLimit: data.applicationLimit,
											existingLimit: data.existingLimit,
											expiredDate: data.expiredDate,
											plafonType: data.plafonType,
											totalLimit: data.totalLimit,
											customerId: data.customerId,
											isDefault: data.isDefault ?? false,
										};
									}),
								debtorPlafons:
									values.totalFacilityRequest.debtorPlafons?.filter(
										(d) => d.plafonType,
									) ?? [],
							},
							establishmentYear:
								'establishmentYear' in values
									? (values.establishmentYear as string)
									: '',
							operationalYear:
								'operationalYear' in values
									? (values.operationalYear as string)
									: '',
							consentApprovalNumber:
								'consentApprovalNumber' in values
									? (values.consentApprovalNumber as string)
									: '',
							consentApprovalDate:
								'consentApprovalDate' in values
									? (values.consentApprovalDate as string)
									: '',
							restruGpStatus:
								'restruGpStatus' in values
									? (values.restruGpStatus as number)
									: 0,
							restruGpCovidStatus:
								'restruGpCovidStatus' in values
									? (values.restruGpCovidStatus as number)
									: 0,
							restruGpDueDate:
								'restruGpDueDate' in values
									? (values.restruGpDueDate as string)
									: '',
							verificationNotes:
								'verificationNotes' in values
									? (values.verificationNotes as string)
									: '',
							contracts:
								'contracts' in values
									? (values.contracts as ProspectEmploymentContract[]).map(
											(contract) => {
												return {
													prospectId,
													id: contract.id!,
													bouwheerId: contract.bouwheer.id,
													contractDetail: contract.contractDetail,
													contractDocument: contract.contractDocument && {
														id: contract.contractDocument.id,
														name: contract.contractDocument.name,
														path: contract.contractDocument.path,
													},
													contractValue: contract.contractValue,
													startDate: contract.startDate,
													endDate: contract.endDate,
													jobLocation: contract.jobLocation,
													jobTitle: contract.jobTitle,
													spkNumber: contract.spkNumber,
													termOfPayment: contract.termOfPayment!,
													tenor: contract.tenor,
													paymentMethod: contract.paymentMethod?.id,
												};
											},
										)
									: [],
						};

						if (groupName === 'FRM') {
							await submitWholesaleJFMutation.mutateAsync(payload);
						}

						if (groupName === 'CCA') {
							const verificationNotes =
								'verificationNotes' in values
									? (values.verificationNotes as string)
									: '';

							await verifyWholesaleJFMutation.mutateAsync({
								...payload,
								verificationNotes,
							});
						}
					}

					if (plafonType === 'NON_JOIN_FINANCING') {
						const payload: NAPWholesalePayload = {
							wholesaleId: values.wholesaleId,
							napId: values.napId,
							authorityLevel: values.authorityLevel,
							bmriReferralInformation: values.bmriReferralInformation,
							debtorFappDate: values.debtorFappDate,
							notes: values.notes,
							prospectId: values.prospectId,
							referralFormDate: values.referralFormDate,
							regulationReference: values.regulationReference,
							totalFacilityRequest: {
								businessGroups: values.totalFacilityRequest.businessGroups
									.filter((data) => data.name)
									.map((data) => {
										return {
											name: data.name,
											applicationLimit: data.applicationLimit,
											existingLimit: data.existingLimit,
											expiredDate: data.expiredDate,
											plafonType: data.plafonType,
											totalLimit: data.totalLimit,
											customerId: data.customerId,
											isDefault: data.isDefault ?? false,
										};
									}),
								debtor: values.totalFacilityRequest.debtor,
								debtorPlafons:
									values.totalFacilityRequest.debtorPlafons?.filter(
										(d) => d.plafonType,
									) ?? [],
							},
							additionalProposers: values.additionalProposers
								.filter((data) => data.isAdditional)
								.map((data) => {
									return { id: data.id };
								}),
							agingDate: values.agingDate,
							additionalRecommendingCommittees:
								values.additionalRecommendingCommittees
									.filter((data) => data.isAdditional)
									.map((data) => {
										return { id: data.id };
									}),
						};

						if (groupName === 'FRM') {
							await submitWholesaleMutation.mutateAsync(payload);
						}

						if (groupName === 'CCA') {
							const verificationNotes =
								'verificationNotes' in values
									? (values.verificationNotes as string)
									: '';

							await verifyWholesaleMutation.mutateAsync({
								...payload,
								verificationNotes,
							});
						}
					}

					onSubmitded();
				}}
			>
				{formFragment()}
				{proposerFragment}
				{committeFragment}
				{debtorFragment}
				{businessGroupFragment}

				<Form.Item
					label="Aging Date"
					style={{ marginTop: 20 }}
					name={fieldName('agingDate')}
					initialValue={data?.agingDate}
					rules={[rule.required()]}
					getValueProps={(value) => ({ value: value && dayjs(value) })}
					normalize={(value) => value && dayjs(value).utc(true).toISOString()}
				>
					<DatePicker
						style={{ width: 200 }}
						disabled={isReadOnly}
					/>
				</Form.Item>

				<Divider />

				{plafonType === 'JOIN_FINANCING' && contractFragment}

				<Form.Item
					label={groupName === 'FRM' ? 'Informasi Tambahan' : 'Notes FRM'}
					style={{ marginTop: 20 }}
					name={fieldName('notes')}
					initialValue={data?.notes}
					rules={groupName === 'FRM' ? [rule.required()] : []}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly || groupName !== 'FRM'}
					/>
				</Form.Item>
				<Divider />
				{groupName !== 'FRM' && (
					<Form.Item
						label={groupName === 'CCA' ? 'Verification Notes' : 'Notes CCA'}
						style={{ marginTop: 20 }}
						name={'verificationNotes'}
						initialValue={data?.verificationNotes}
						rules={groupName === 'CCA' ? [rule.required()] : []}
					>
						<TextArea
							rows={3}
							disabled={isReadOnly || groupName !== 'CCA'}
						/>
					</Form.Item>
				)}

				<Button
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 40,
					}}
					disabled={
						submitWholesaleMutation.isPending ||
						submitWholesaleJFMutation.isPending ||
						verifyWholesaleMutation.isPending ||
						isReadOnly
					}
					loading={
						submitWholesaleMutation.isPending ||
						submitWholesaleJFMutation.isPending ||
						verifyWholesaleMutation.isPending
					}
				>
					NEXT
				</Button>
			</Form>
		</Fragment>
	);
};

export default NAPWholesale;
